/**
 * Page-management handlers for the flipbook, shared by both surfaces that
 * drive it — the bottom filmstrip (FlipbookBar) and the Animation panel's
 * Flipbook mode (FlipbookPanel). There is ONE implementation of each page
 * mutation; both components call this hook rather than duplicating the glue.
 *
 * Ephemeral UI state (activeId/fps/…) comes from FlipbookContext; the scene is
 * the source of truth for pages, read fresh on every call.
 */
import { useCallback } from "react";

import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import { arrayToMap, randomId } from "@excalidraw/common";
import {
  duplicateElements,
  getElementsOverlappingFrame,
  newFrameElement,
  syncInvalidIndices,
  withAnimationOverridesSuspended,
} from "@excalidraw/element";

import type {
  ExcalidrawElement,
  ExcalidrawFrameLikeElement,
} from "@excalidraw/element/types";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import {
  FLIPBOOK_PAGE_KEY,
  flipbookPageCustomData,
  getFlipbookBooks,
  getFlipbookPages,
  getPageBookId,
  planInsertAfter,
  planNewBookOrigin,
  planReorder,
} from "./ops";
import { useFlipbook } from "./FlipbookContext";

type API = ExcalidrawImperativeAPI | null;

/**
 * The book every action operates on: the explicitly selected one when it still
 * exists, else the scene's first book. Resolved against the scene on each call
 * rather than stored, so deleting a book's last page can't strand the UI on an
 * id nothing matches.
 */
export const resolveBookId = (
  elements: readonly ExcalidrawElement[],
  activeBookId: string | null,
): string | null => {
  const books = getFlipbookBooks(elements);
  if (activeBookId && books.some((b) => b.id === activeBookId)) {
    return activeBookId;
  }
  return books[0]?.id ?? null;
};

export const useFlipbookActions = (excalidrawAPI: API) => {
  const { fps, setFps, setActiveId, activeBookId, setActiveBookId } =
    useFlipbook();

  /** active book id resolved against the live scene (see resolveBookId).
   * useCallback so the handlers below can depend on it without re-creating
   * themselves on every render. */
  const bookId = useCallback(
    () => resolveBookId(excalidrawAPI?.getSceneElements() ?? [], activeBookId),
    [excalidrawAPI, activeBookId],
  );

  const goTo = useCallback(
    (frame: ExcalidrawFrameLikeElement, animate: boolean) => {
      setActiveId(frame.id);
      excalidrawAPI?.setViewport({
        target: [frame],
        fit: "scale-down",
        animation: animate,
      });
    },
    [excalidrawAPI, setActiveId],
  );

  // mutateElement alone never records an undo entry
  const captureUndo = useCallback(() => {
    excalidrawAPI?.updateScene({
      elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
  }, [excalidrawAPI]);

  /** Shifts later frames right and returns the physical slot opened after
   * `ref`; shared by "add page" and "duplicate page". */
  const openSlotAfter = useCallback(
    (ref: ExcalidrawFrameLikeElement) => {
      const scene = excalidrawAPI!.getSceneElements();
      const plan = planInsertAfter(scene, ref);
      const shiftSet = new Set(plan.shiftIds);
      for (const el of scene) {
        if (shiftSet.has(el.id)) {
          excalidrawAPI!.mutateElement(el, { x: el.x + plan.offsetX });
        }
      }
      return plan;
    },
    [excalidrawAPI],
  );

  const addPage = useCallback(
    (ref?: ExcalidrawFrameLikeElement) => {
      if (!excalidrawAPI) {
        return;
      }
      // the page joins the book its neighbour belongs to; with no neighbour
      // (first page of the scene, or of a brand-new book) it joins the active
      // one, falling back to the legacy id so a fresh scene has a book at all
      const targetBook =
        (ref && getPageBookId(ref)) ??
        bookId() ??
        flipbookPageCustomData()[FLIPBOOK_PAGE_KEY];
      const pages = getFlipbookPages(
        excalidrawAPI.getSceneElements(),
        targetBook,
      );
      // new pages tick at the current flipbook rate and carry the page marker
      const pageDuration = flipbookPageCustomData(
        { screenDurationMs: Math.round(1000 / fps) },
        targetBook,
      );
      let frame: ExcalidrawFrameLikeElement;
      if (ref) {
        const plan = openSlotAfter(ref);
        frame = newFrameElement({
          x: ref.x + plan.offsetX,
          y: ref.y,
          width: ref.width,
          height: ref.height,
          name: `Frame ${pages.length + 1}`,
          customData: pageDuration,
        });
        const scene = excalidrawAPI.getSceneElements();
        excalidrawAPI.updateScene({
          // the new frame carries index:null — sync so the mid-array insert
          // satisfies the fractional-index invariant
          elements: syncInvalidIndices([
            ...scene.slice(0, plan.insertIndex),
            frame,
            ...scene.slice(plan.insertIndex),
          ]),
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });
      } else {
        const state = excalidrawAPI.getAppState();
        frame = newFrameElement({
          x: -state.scrollX + 80,
          y: -state.scrollY + 80,
          width: 640,
          height: 480,
          name: "Frame 1",
          customData: pageDuration,
        });
        excalidrawAPI.updateScene({
          elements: syncInvalidIndices([
            ...excalidrawAPI.getSceneElements(),
            frame,
          ]),
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });
      }
      goTo(frame, true);
    },
    [excalidrawAPI, fps, goTo, openSlotAfter, bookId],
  );

  const duplicatePage = useCallback(
    (ref: ExcalidrawFrameLikeElement) => {
      if (!excalidrawAPI) {
        return;
      }
      const plan = openSlotAfter(ref);
      const scene = excalidrawAPI.getSceneElements();
      const pageElements = [
        ref,
        ...getElementsOverlappingFrame(scene, ref, arrayToMap(scene)),
      ];
      // in-place duplication inserts clones next to their originals in the
      // array — the clone frame lands before the next frame, keeping order.
      // deepCopyElement copies customData, so the clone stays a flipbook page.
      const { elementsWithDuplicates } = duplicateElements({
        type: "in-place",
        elements: scene,
        idsOfElementsToDuplicate: arrayToMap(pageElements),
        appState: { editingGroupId: null, selectedGroupIds: {} },
        randomizeSeed: false,
        overrides: ({ origElement }) => ({ x: origElement.x + plan.offsetX }),
      });
      excalidrawAPI.updateScene({
        // in-place clones inherit their originals' indices — sync so the
        // strictly-increasing fractional-index invariant holds
        elements: syncInvalidIndices(elementsWithDuplicates),
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });
      const pageIds = new Set(
        getFlipbookPages(scene, getPageBookId(ref) ?? undefined).map(
          (f) => f.id,
        ),
      );
      const clone = elementsWithDuplicates.find(
        (el) =>
          el.type === ref.type &&
          el.x === ref.x + plan.offsetX &&
          el.y === ref.y &&
          !pageIds.has(el.id),
      );
      if (clone) {
        goTo(clone as ExcalidrawFrameLikeElement, true);
      }
    },
    [excalidrawAPI, goTo, openSlotAfter],
  );

  const removeIds = useCallback(
    (ids: Set<string>) => {
      if (!excalidrawAPI) {
        return;
      }
      excalidrawAPI.updateScene({
        elements: excalidrawAPI
          .getSceneElements()
          .filter((el) => !ids.has(el.id)),
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });
    },
    [excalidrawAPI],
  );

  const applyFps = useCallback(
    (value: number) => {
      if (!excalidrawAPI) {
        return;
      }
      const clamped = Math.min(30, Math.max(1, Math.round(value) || 1));
      setFps(clamped);
      const durationMs = Math.round(1000 / clamped);
      // rate is per book — retiming this flipbook must not retime the others
      for (const frame of getFlipbookPages(
        excalidrawAPI.getSceneElements(),
        bookId() ?? undefined,
      )) {
        excalidrawAPI.mutateElement(frame, {
          customData: { ...frame.customData, screenDurationMs: durationMs },
        });
      }
      captureUndo();
    },
    [excalidrawAPI, setFps, captureUndo, bookId],
  );

  /** Drag-and-drop reorder: move the dragged page so it lands at `toIndex`
   * in sequence order; array order and physical slots both follow. */
  const reorderPages = useCallback(
    (fromId: string, toIndex: number) => {
      if (!excalidrawAPI) {
        return;
      }
      const scene = excalidrawAPI.getSceneElements();
      const book =
        getPageBookId(
          scene.find((el) => el.id === fromId) ?? ({} as ExcalidrawElement),
        ) ?? undefined;
      const currentIds = getFlipbookPages(scene, book).map((f) => f.id);
      const fromIndex = currentIds.indexOf(fromId);
      if (fromIndex === -1 || fromIndex === toIndex) {
        return;
      }
      const newOrder = [...currentIds];
      newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, fromId);
      const plan = planReorder(scene, newOrder, book);
      const byId = new Map(plan.elements.map((el) => [el.id, el]));
      for (const move of plan.moves) {
        const el = byId.get(move.id);
        if (el) {
          excalidrawAPI.mutateElement(el, { x: move.x, y: move.y });
        }
      }
      excalidrawAPI.updateScene({
        elements: syncInvalidIndices(plan.elements),
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });
      goTo(byId.get(fromId) as ExcalidrawFrameLikeElement, true);
    },
    [excalidrawAPI, goTo],
  );

  /**
   * Starts a new, independent flipbook: one blank page placed below every
   * existing book, which then becomes the active one.
   */
  const addBook = useCallback(() => {
    if (!excalidrawAPI) {
      return;
    }
    const scene = excalidrawAPI.getSceneElements();
    const state = excalidrawAPI.getAppState();
    const origin = planNewBookOrigin(scene, {
      x: -state.scrollX + 80,
      y: -state.scrollY + 80,
    });
    const newBookId = `book-${randomId()}`;
    const frame = newFrameElement({
      ...origin,
      width: 640,
      height: 480,
      name: "Frame 1",
      customData: flipbookPageCustomData(
        { screenDurationMs: Math.round(1000 / fps) },
        newBookId,
      ),
    });
    excalidrawAPI.updateScene({
      elements: syncInvalidIndices([...scene, frame]),
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
    setActiveBookId(newBookId);
    goTo(frame, true);
  }, [excalidrawAPI, fps, goTo, setActiveBookId]);

  /** Deletes a whole book — every page of it and their contents. */
  const removeBook = useCallback(
    (id: string) => {
      if (!excalidrawAPI) {
        return;
      }
      const scene = excalidrawAPI.getSceneElements();
      const pageIds = new Set(getFlipbookPages(scene, id).map((f) => f.id));
      excalidrawAPI.updateScene({
        elements: scene.filter(
          (el) =>
            !pageIds.has(el.id) && !(el.frameId && pageIds.has(el.frameId)),
        ),
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });
      setActiveBookId(null);
      setActiveId(null);
    },
    [excalidrawAPI, setActiveBookId, setActiveId],
  );

  /** Runs a synchronous draw with all animation overrides detached, so
   * neither onion ghosts nor playback page-swaps leak into thumbnails. */
  const withGhostsSuspended = useCallback((draw: () => void) => {
    withAnimationOverridesSuspended(draw);
  }, []);

  return {
    goTo,
    captureUndo,
    addPage,
    addBook,
    removeBook,
    duplicatePage,
    removeIds,
    applyFps,
    reorderPages,
    withGhostsSuspended,
  };
};
